'use client';

import type { MouseEvent, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import type { PressEvent } from '@frameio/vapor';

type FakeBoundingRect = {
  getBoundingClientRect: () => DOMRect;
};

type ContextMenuState<T> = {
  isOpen: boolean;
  position: FakeBoundingRect | undefined;
  target: T | undefined;
};

type ContextMenuContextValue<T> = {
  setState:
    | React.Dispatch<React.SetStateAction<ContextMenuState<T>>>
    | undefined;
  state: ContextMenuState<T>;
};

const initialState: ContextMenuState<any> = {
  isOpen: false,
  position: undefined,
  target: undefined,
};

const ContextMenuContext = createContext<ContextMenuContextValue<any>>({
  setState: undefined,
  state: initialState,
});

export function ContextMenuContextProvider<T>({ children }: PropsWithChildren) {
  const [state, setState] = useState<ContextMenuState<T>>(initialState);
  const value = useMemo(
    () => ({
      setState,
      state,
    }),
    [state]
  );

  return (
    <ContextMenuContext.Provider value={value}>
      <>{children}</>
    </ContextMenuContext.Provider>
  );
}

export const useContextMenuIsOpen = () =>
  useContextSelector(ContextMenuContext, ({ state }) => state.isOpen);

export const useContextMenuPosition = () =>
  useContextSelector(ContextMenuContext, ({ state }) => state.position);

export const useContextMenuTarget: <T>() => T = () =>
  useContextSelector(ContextMenuContext, ({ state }) => state.target);

export const useIsContextMenuTarget: <T>(t: T) => boolean = (t) =>
  useContextSelector(ContextMenuContext, ({ state }) => state.target === t);

export const useCloseContextMenu = () => {
  const setState = useContextSelector(
    ContextMenuContext,
    (value) => value.setState
  );

  useEffect(() => {
    return () => {
      setState?.({
        isOpen: false,
        position: undefined,
        target: undefined,
      });
    };
  }, [setState]);

  return useCallback(() => {
    if (!setState) return;

    setState(() => ({
      isOpen: false,
      position: undefined,
      target: undefined,
    }));
  }, [setState]);
};

export function useOpenContextMenu<T, E extends HTMLElement>() {
  const setState = useContextSelector(
    ContextMenuContext,
    (value) => value.setState
  );

  return useCallback(
    (
      target: T | undefined,
      el: E | null,
      event: MouseEvent | PressEvent | null = null
    ) => {
      if (!setState) return;

      let position: FakeBoundingRect | null = null;

      /**
       * This process of updating the context menu location is complicated, but
       * necessary. The purpose is to prevent the menu from completely closing
       * when the user right clicks a different folder _while_ the context menu
       * is still open. The pattern is recommended by vapor and can be seen here:
       *
       * https://github.com/Frameio/vapor/blob/main/packages/vapor/src/components/Menu/stories/Examples/VirtualTarget.tsx
       * https://main--5de9642270f71d0020378e20.chromatic.com/?path=/story/navigation-menu-stories--contextual-menu
       */

      let x = 0;
      let y = 0;

      if (event && 'pageX' in event && 'pageY' in event) {
        x = event.pageX;
        y = event.pageY;
      } else if (event && 'x' in event && 'y' in event) {
        x = event.x as number;
        y = event.y as number;
      }
      // x and y are both 0 when the click event was triggered by a space bar press
      if ((!event || (x === 0 && y === 0)) && el) {
        const domRect = el.getBoundingClientRect();

        x = domRect.x + Math.floor(domRect.width / 2);
        y = domRect.y + Math.floor(domRect.height / 2);
      }

      position = {
        getBoundingClientRect: () => ({
          x,
          y,
          left: x,
          top: y,
          right: x,
          bottom: y,
          width: 0,
          height: 0,
          toJSON: () => '',
        }),
      };

      const setPosition = () => {
        setState({
          isOpen: true,
          position,
          target,
        });
      };

      setState(({ isOpen, target: prevTarget }) => {
        setTimeout(
          () => {
            setPosition();
          },
          // only want the delay if a menu is already open
          isOpen ? REPOSITION_MENU_DELAY : 0
        );

        return {
          isOpen: false,
          position: undefined,
          target: prevTarget,
        };
      });
    },
    [setState]
  );
}

const REPOSITION_MENU_DELAY = 150;
